import { forwardRef, InputHTMLAttributes } from 'react'
import { LucideIcon } from 'lucide-react'

import {
  CONTROL_BASE,
  CONTROL_INVALID,
  CONTROL_SIZES,
  CONTROL_TEXT,
  ControlSize,
  describedByIds,
  FieldProps,
  FieldShell,
  useControlId,
} from './Field'

/**
 * LE CHAMP TEXTE UNIQUE (LOT-H6).
 *
 * Contrat : `docs/design/DESIGN-SYSTEM.md` §3 (métriques), §4 (rayons), §1
 * (anneau de focus `primary`).
 *
 * Il enveloppe un `<input>` natif — c'est le seul contrôle dont l'élément natif
 * n'a AUCUN chrome imposé par le système : un `<input type="text">` correctement
 * habillé est indiscernable d'un champ dessiné, contrairement à `<select>` et
 * `<input type="date">` qui traînent leur chevron et leur glyphe de calendrier.
 * Le remplacer par un `contenteditable` ne gagnerait rien et perdrait la saisie
 * assistée d'iOS (autocomplétion, clavier adapté au `type`, `inputMode`).
 */

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    FieldProps {
  /**
   * `sm` (32 px) est réservé aux barres de filtres denses. Contrairement au
   * `Button`, un `<input>` est un élément REMPLACÉ : il n'accepte pas de
   * pseudo-élément, donc la cible tactile de 44 px ne peut pas être rétablie
   * par-dessous. C'est la seule raison pour laquelle `md` reste le défaut.
   */
  size?: ControlSize
  /** Icône décorative à gauche (loupe d'une recherche, enveloppe d'un e-mail). */
  icon?: LucideIcon
  /** Élément collé à droite DANS le champ (bouton « afficher le mot de passe »). */
  trailing?: React.ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    id,
    size = 'md',
    icon: Icon,
    trailing,
    label,
    hint,
    error,
    required,
    wrapperClassName = '',
    className = '',
    type = 'text',
    ...rest
  },
  ref
) {
  const controlId = useControlId(id)
  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'

  return (
    <FieldShell
      controlId={controlId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      wrapperClassName={wrapperClassName}
    >
      <div className="relative">
        {Icon && (
          <Icon
            className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${iconSize} text-foreground-tertiary`}
            aria-hidden="true"
          />
        )}
        <input
          ref={ref}
          id={controlId}
          type={type}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedByIds(controlId, hint, error)}
          className={[
            CONTROL_BASE,
            CONTROL_SIZES[size],
            CONTROL_TEXT,
            error ? CONTROL_INVALID : '',
            Icon ? (size === 'sm' ? 'pl-8' : 'pl-9') : '',
            trailing ? 'pr-11' : '',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          {...rest}
        />
        {trailing && (
          <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center">
            {trailing}
          </div>
        )}
      </div>
    </FieldShell>
  )
})

/**
 * Zone de saisie multiligne — même chrome, hauteur libre.
 *
 * `h-control` n'a pas de sens ici : c'est le nombre de lignes qui fait la
 * hauteur. On garde le padding vertical du champ md pour que la première ligne
 * s'aligne avec celle d'un `Input` posé à côté.
 */
export interface TextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    FieldProps {}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { id, label, hint, error, required, wrapperClassName = '', className = '', rows = 4, ...rest },
  ref
) {
  const controlId = useControlId(id)

  return (
    <FieldShell
      controlId={controlId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      wrapperClassName={wrapperClassName}
    >
      <textarea
        ref={ref}
        id={controlId}
        rows={rows}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedByIds(controlId, hint, error)}
        className={[
          CONTROL_BASE,
          CONTROL_TEXT,
          'px-3 py-2.5',
          error ? CONTROL_INVALID : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      />
    </FieldShell>
  )
})

export default Input
